'use client';

import React from 'react';
import { useCRM } from 'src/context/crm';
import { Checkbox } from 'src/components/PulseUI';
import GridStatusFilterMenuItem from './GridStatusFilterMenuItem';
import GridStatusFilterMenuHeader from './GridStatusFilterMenuHeader';

interface StatusFieldOption {
  bucket: string;
  value: string;
  label: string;
  color?: string;
  order?: number;
}

interface StatusField {
  options?: StatusFieldOption[];
}

interface GridStatusFilterMenuGroupProps {
  bucket: string;
  statusField?: StatusField;
  groupedOptions: Record<string, StatusFieldOption[]>;
}

const GridStatusFilterMenuGroup: React.FC<GridStatusFilterMenuGroupProps> = ({
  bucket,
  statusField,
  groupedOptions,
}) => {
  const {
    statusFilterOptions,
    setStatusFilterOptions,
    selectedBuckets,
    setSelectedBuckets,
  } = useCRM();

  // Select all options in a bucket
  const handleSelectAll = ({ bucket }: { bucket: string }) => {
    if (!bucket) return;

    const options = statusField?.options
      ?.filter((option) => {
        return option.bucket === bucket;
      })
      .map((option) => option.value);

    // If the bucket was previously selected, remove all its options from statusFilterOptions
    // If the bucket was not previously selected, add all its options to statusFilterOptions
    const isBucketSelected = selectedBuckets?.includes(bucket);
    const newOptions = isBucketSelected
      ? statusFilterOptions.filter((option) => !options.includes(option))
      : [
          ...statusFilterOptions,
          ...options.filter((option) => !statusFilterOptions.includes(option)),
        ];

    setStatusFilterOptions(newOptions);

    // If the bucket was previously selected, remove it from selectedBuckets
    // If the bucket was not previously selected, add it to selectedBuckets
    const newBuckets = isBucketSelected
      ? selectedBuckets?.filter((b) => b !== bucket)
      : [...(selectedBuckets || []), bucket];

    setSelectedBuckets(newBuckets);
  };

  return (
    <ul className="list-none p-0 m-0 text-sm">
      <li>
        <div className="flex items-center cursor-pointer py-1 px-2">
          <Checkbox
            size="sm"
            checked={selectedBuckets?.includes(bucket) || false}
            onChange={() => handleSelectAll({ bucket })}
          />
          <GridStatusFilterMenuHeader bucket={bucket} />
        </div>
        <ul className="list-none p-0 m-0">
          {groupedOptions[bucket]?.map((option, index) => (
            <GridStatusFilterMenuItem
              key={`option_${index}`}
              option={option}
              bucket={bucket}
            />
          ))}
        </ul>
      </li>
    </ul>
  );
};

export default GridStatusFilterMenuGroup;
